import {
  GroupInfo,
  UserLoginInfo,
  UserSetting
} from "@/services/login-service/entities/UserLoginEntity";
import { AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import { app } from "@microsoft/teams-js";
import { View, Views } from "react-big-calendar";
import { create } from "zustand";
import userStore from "../users";
import calendarStore from "../calendar";
import { UserService } from "@/services/user-service/UserService";
import { showErrorToast } from "@/utils/toastUtils";
import { graphService } from "@/services/graph-service";
import { graphScopes, msalConfig, msalInstance, opScopes } from "@/config/authConfig";
import { CUSTOM_VIEWS } from "@/pages/home-page/util";
import { queryClient } from "@/main";
import versionManagerStore from "../version";
import notificationStore from "../notification";
import i18next from "i18next";
import { TeamsAppAuth } from "./teamAuth";

type State = {
  isAuthenticated: boolean;
  isLoading: boolean;
  loadingUserDataMessage: string;
  user: UserLoginInfo;
  accounts: AccountInfo[];
  isCheckingAuth: boolean;
  messageFailed: string;
};

type Action = {
  initializeGraphService: () => Promise<void>;
  handleWebAuth: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  fetchUserData: () => Promise<void>;
  login: (account?: AccountInfo) => Promise<void>;
  loginWithDifferentAccount: () => Promise<void>;
  switchAccount: (accountId: string) => Promise<void>;
  logout: () => Promise<void>;
  getLastLoggedInUser: () => AccountInfo | null;
  updateUserSettings: (settings: Partial<UserSetting>) => void;
  updateUserGroups: (customGroups: GroupInfo[]) => void;
  setIsCheckingAuth: (isCheckingAuth: boolean) => void;
  reloadUserData: () => Promise<void>;

  setAllAccounts: (accounts: AccountInfo[]) => void;
  removeAccountWhenLoginFailed: (result: AuthenticationResult | null) => Promise<void>;
};

const getTeamsContext = async () => {
  try {
    await app.initialize();
    return await app.getContext();
  } catch {
    return null;
  }
};

const authStore = create<State & Action>()((set, get) => ({
  isAuthenticated: false,
  isLoading: false,
  loadingUserDataMessage: "",
  user: new UserLoginInfo(),
  accounts: [],
  isCheckingAuth: false,
  messageFailed: "",

  setAllAccounts: (accounts: AccountInfo[]) => set({ accounts }),
  setIsCheckingAuth: (isCheckingAuth: boolean) => set({ isCheckingAuth }),
  initializeGraphService: async () => {
    try {
      const activeAccount = msalInstance.getActiveAccount();
      if (!activeAccount) {
        throw new Error("No active account");
      }

      const graphToken = await msalInstance.acquireTokenSilent({
        ...graphScopes,
        account: activeAccount
      });

      await graphService.initializeWithToken(graphToken?.accessToken, graphToken?.expiresOn);
    } catch (error) {
      console.error("Failed to initialize Graph service:", error);
      showErrorToast(
        "Failed to initialize Graph service",
        "Something went wrong. Please sign in again."
      );
    }
  },

  handleWebAuth: async () => {
    let result: AuthenticationResult | null = null;

    try {
      if (!localStorage.getItem("lastLoggedInUser")) {
        return;
      }

      let activeAccount = msalInstance.getActiveAccount();

      if (!activeAccount) {
        const lastLoggedInUser = get().getLastLoggedInUser();
        activeAccount = lastLoggedInUser;

        if (!activeAccount) {
          return;
        }
      }

      try {
        result = await msalInstance.acquireTokenSilent({
          ...opScopes,
          account: activeAccount!
        });
      } catch (error: any) {
        console.log("Could not acquireTokenSilent", error);
        set({ messageFailed: "Something went wrong. Please sign in again." });
        return;
      }

      if (result && result.accessToken) {
        await get().initializeGraphService();
        await get().fetchUserData();
        set({ messageFailed: "" });
        localStorage.setItem("lastLoggedInUser", JSON.stringify(result.account));
      }

      return;
    } catch (error: any) {
      console.log("handleWebAuth error", error);

      await get().removeAccountWhenLoginFailed(result);

      setTimeout(() => {
        showErrorToast(
          i18next.t("login.failed.title", "Login failed"),
          i18next.t("login.failed.message", "Login failed, please try again later.")
        );
      }, 100);
    }
  },

  checkAuthStatus: async () => {
    const { isCheckingAuth, handleWebAuth } = get();
    if (isCheckingAuth) return;

    set({ isCheckingAuth: true });

    try {
      const context = await getTeamsContext();
      if (context) {
        await TeamsAppAuth.handleTeamsAuth();
        set({ isCheckingAuth: false });
        return;
      }

      await handleWebAuth();
    } catch (error) {
      console.error("Authentication error:", error);
    } finally {
      set({ isCheckingAuth: false });
    }
  },

  reloadUserData: async () => {
    const userService = new UserService();
    const res = await userService.getUserLoginInformation();
    if (!res.error) {
      const { user: prevUser } = get();

      const dataVersionInfo = {
        version: res.data.version,
        isOverrideReadAccess: res.data.setting?.isOverrideReadAccess ?? false
      };

      if (versionManagerStore.getState().isInvalidMajorVersion(dataVersionInfo)) {
        queryClient.clear();
      }

      set({
        user: {
          ...prevUser,
          ...res.data,
          isInTeams: prevUser.isInTeams,
          teamsToken: prevUser.teamsToken
        }
      });

      versionManagerStore.getState().setVersion(dataVersionInfo);
      const defaultGroupId = res.data.setting?.defaultGroup || "";
      userStore.getState().initializeSelectedUsers(
        res.data.groups.sort((a, b) => a.groupName.localeCompare(b.groupName)),
        defaultGroupId,
        res.data?.setting?.groupOrder
      );
      return;
    }
  },
  fetchUserData: async () => {
    try {
      set({ isLoading: true });
      const userService = new UserService();
      set({ loadingUserDataMessage: "Loading user data..." });
      const res = await userService.getUserLoginInformation();
      if (!res.error) {
        const { user: prevUser } = get();

        const dataVersionInfo = {
          version: res.data.version,
          isOverrideReadAccess: res.data.setting?.isOverrideReadAccess ?? false
        };

        if (versionManagerStore.getState().isInvalidMajorVersion(dataVersionInfo)) {
          queryClient.clear();
        }

        if (res.data.setting?.language === "english") {
          res.data.setting.language = "en";
        }

        set({
          user: {
            ...prevUser,
            ...res.data,
            isInTeams: prevUser.isInTeams,
            teamsToken: prevUser.teamsToken
          }
        });

        i18next.changeLanguage(res.data.setting?.language);

        versionManagerStore.getState().setVersion(dataVersionInfo);

        const defaultGroupId = res.data.setting?.defaultGroup || "";
        userStore.getState().initializeSelectedUsers(
          res.data.groups.sort((a, b) => a.groupName.localeCompare(b.groupName)),
          defaultGroupId,
          res.data?.setting?.groupOrder
        );

        calendarStore.getState().setAppliedFilter(null);
        calendarStore.getState().setFilterItems([]);

        if (res.data.setting?.defaultView) {
          const defaultView = res.data.setting.defaultView;
          let viewToSet: View = Views.DAY;
          switch (defaultView.toLowerCase()) {
            case "week":
              viewToSet = Views.WEEK;
              break;
            case "day":
              viewToSet = Views.DAY;
              break;
            case "workweek":
              viewToSet = Views.WORK_WEEK;
              break;
            case "list":
              viewToSet = CUSTOM_VIEWS.LIST;
              break;
            case "timeline":
              viewToSet = CUSTOM_VIEWS.TIMELINE;
              break;
            default:
              viewToSet = Views.DAY;
          }

          calendarStore.getState().initializeDefaultSelectedDate(viewToSet);
        }

        // Set default timeline range
        calendarStore.getState().initializeTimelineTimeRange();

        set({
          loadingUserDataMessage: "",
          isAuthenticated: true
        });

        return;
      }

      set({ loadingUserDataMessage: "Failed to load user data, please retry" });
      throw new Error("Failed to load user data");
    } catch (error: any) {
      set({ loadingUserDataMessage: "Failed to load user data, please retry" });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  login: async (account?: AccountInfo): Promise<void> => {
    set({ isLoading: true });

    const context = await getTeamsContext();
    if (context) {
      await TeamsAppAuth.handleTeamsAuth();
      return;
    }

    let result: AuthenticationResult | null = null;
    try {
      try {
        result = await msalInstance.acquireTokenSilent({
          ...opScopes,
          account: account!
        });
      } catch (error: any) {
        result = await msalInstance.acquireTokenPopup({
          ...opScopes,
          account: account!
        });
      }

      if (result && result.account && result.accessToken) {
        msalInstance.setActiveAccount(result.account);
        await get().initializeGraphService();
        await get().fetchUserData();
        const allAccounts = msalInstance.getAllAccounts();
        set({ accounts: allAccounts });
        localStorage.setItem("lastLoggedInUser", JSON.stringify(result.account));
      }
    } catch (error: any) {
      console.error(error);
      set({
        loadingUserDataMessage: i18next.t(
          "login.failed.message",
          "Login failed, please try again later."
        )
      });

      await get().removeAccountWhenLoginFailed(result);

      showErrorToast(
        i18next.t("login.failed.title", "Login failed"),
        i18next.t("login.failed.message", "Login failed, please try again later.")
      );
    } finally {
      set({ isLoading: false });
    }
  },

  removeAccountWhenLoginFailed: async (result: AuthenticationResult | null) => {
    if (result?.account) {
      msalInstance.setActiveAccount(null);
      const cacheKey = result.account.homeAccountId;
      Object.keys(localStorage).forEach((key) => {
        if (key.includes(cacheKey)) {
          localStorage.removeItem(key);
        }
      });
    }

    const allAccounts = msalInstance.getAllAccounts();
    const defaultAccountActives = allAccounts.filter(
      (x) => x.homeAccountId !== result?.account?.homeAccountId
    );

    if (defaultAccountActives?.length) {
      const userLogined = authStore.getState().user;
      let accountActiveMSAL: AccountInfo | null = defaultAccountActives[0];
      if (userLogined) {
        const accountActiveMSALDefault = defaultAccountActives.find(
          (x) => x.username === userLogined?.upn
        );

        if (accountActiveMSALDefault) {
          accountActiveMSAL = accountActiveMSALDefault;
        }
      }

      if (accountActiveMSAL) {
        msalInstance.setActiveAccount(accountActiveMSAL);
      }

      await get().fetchUserData();
      await get().initializeGraphService();
    }
  },

  loginWithDifferentAccount: async (): Promise<void> => {
    set({ isLoading: true });

    let result: AuthenticationResult | null = null;
    try {
      result = await msalInstance.loginPopup({
        ...opScopes,
        prompt: "select_account"
      });

      if (result && result.account && result.accessToken) {
        msalInstance.setActiveAccount(result.account);
        await get().initializeGraphService();
        await get().fetchUserData();
        const allAccounts = msalInstance.getAllAccounts();
        set({ accounts: allAccounts });
        localStorage.setItem("lastLoggedInUser", JSON.stringify(result.account));
      }
    } catch (error: any) {
      console.error(error);

      await get().removeAccountWhenLoginFailed(result);

      showErrorToast(
        i18next.t("login.failed.title", "Login failed"),
        i18next.t("login.failed.message", "Login failed, please try again later.")
      );
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  switchAccount: async (accountId: string): Promise<void> => {
    set({ isLoading: true });

    try {
      if (!msalInstance) {
        throw new Error("MSAL instance not initialized");
      }

      const allAccounts = msalInstance.getAllAccounts();
      const targetAccount = allAccounts.find((acc) => acc.homeAccountId === accountId);

      if (!targetAccount) {
        throw new Error("Account not found");
      }

      msalInstance.setActiveAccount(targetAccount);
      const result = await msalInstance.acquireTokenSilent({
        ...opScopes,
        account: targetAccount
      });

      if (result.accessToken) {
        await get().initializeGraphService();
        await get().fetchUserData();
        localStorage.setItem("lastLoggedInUser", JSON.stringify(targetAccount));

        const userService = new UserService();
        set({ loadingUserDataMessage: "Loading user data..." });
        const res = await userService.getUserLoginInformation();

        if (!res.error) {
          const { user: prevUser } = get();
          set({
            user: {
              ...prevUser,
              ...res.data,
              isInTeams: prevUser.isInTeams,
              teamsToken: prevUser.teamsToken
            }
          });

          const defaultGroupId = res.data.setting?.defaultGroup || "";
          await userStore.getState().initializeSelectedUsers(
            res.data.groups.sort((a, b) => a.groupName.localeCompare(b.groupName)),
            defaultGroupId,
            res.data?.setting?.groupOrder
          );
          set({ loadingUserDataMessage: "" });
        } else {
          throw new Error("Failed to load user data");
        }
      }
    } catch (error: any) {
      showErrorToast(
        i18next.t("login.failed.title", "Login failed"),
        i18next.t("login.failed.message", "Login failed, please try again later.")
      );
      throw error;
    } finally {
      set({ isLoading: false, loadingUserDataMessage: "" });
    }
  },

  logout: async (): Promise<void> => {
    set({ isLoading: true });

    localStorage.removeItem("lastLoggedInUser");
    calendarStore.getState().clearDefaultSelectedDateFromSessionStorage();
    calendarStore.getState().clearTimelineTimeRangeFromSessionStorage();
    userStore.getState().clearDefaultSelectedGroupsFromSessionStorage();
    userStore.getState().resetPageSize();
    notificationStore.getState().clearNotifications();

    set({
      loadingUserDataMessage: "",
      isAuthenticated: false,
      user: new UserLoginInfo()
    });

    graphService.clear();

    Object.keys(localStorage).forEach((key) => {
      if (key.includes("msal")) {
        localStorage.removeItem(key);
      }
    });
    set({ isLoading: false });
  },

  getLastLoggedInUser: (): AccountInfo | null => {
    try {
      const activeAccount = msalInstance.getActiveAccount();
      if (activeAccount) {
        return activeAccount;
      }

      const clientId = msalConfig.auth.clientId;
      const activeAccountFiltersKey = `msal.${clientId}.active-account-filters`;

      const storedFilters = localStorage.getItem(activeAccountFiltersKey);
      if (!storedFilters) {
        return null;
      }

      const filters = JSON.parse(storedFilters);
      const homeAccountId = filters.homeAccountId;

      const savedUserInfo = localStorage.getItem("lastLoggedInUser");
      if (savedUserInfo) {
        const userInfo = JSON.parse(savedUserInfo);

        if (userInfo.homeAccountId === homeAccountId) {
          return userInfo;
        } else {
          localStorage.removeItem("lastLoggedInUser");
          return null;
        }
      }

      return null;
    } catch (error) {
      console.error("Failed to get last logged in user:", error);
      return null;
    }
  },

  updateUserSettings: (settings: Partial<UserSetting>) => {
    const { user } = get();
    const currentSetting = user.setting;
    user.setting = currentSetting
      ? new UserSetting({
          id: currentSetting.id,
          defaultView: settings.defaultView ?? currentSetting.defaultView,
          defaultZoomInTimeLine:
            settings.defaultZoomInTimeLine ?? currentSetting.defaultZoomInTimeLine,
          categoryColors: settings.categoryColors ?? currentSetting.categoryColors,
          language: settings.language ?? currentSetting.language,
          timeScale: settings.timeScale ?? currentSetting.timeScale,
          firstDayOfWeek: settings.firstDayOfWeek ?? currentSetting.firstDayOfWeek,
          defaultGroup: settings.defaultGroup ?? currentSetting.defaultGroup,
          displayGroup: settings.displayGroup ?? currentSetting.displayGroup,
          dateFormat: settings.dateFormat ?? currentSetting.dateFormat,
          timeFormat: settings.timeFormat ?? currentSetting.timeFormat,
          timeZone: settings.timeZone ?? currentSetting.timeZone
        })
      : new UserSetting(settings);
    set({
      user: user
    });
  },

  updateUserGroups: (customGroups: GroupInfo[]) => {
    const { user } = get();
    const regularGroups = user.groups
      .sort((a, b) => a.groupName.localeCompare(b.groupName))
      .filter((g) => !g.isCustomGroup);
    const updatedGroups = [...regularGroups, ...customGroups];
    const defaultGroupId = user.setting?.defaultGroup || "";

    userStore
      .getState()
      .initializeSelectedUsers(updatedGroups, defaultGroupId, user.setting?.groupOrder);

    set({
      user: {
        ...user,
        groups: updatedGroups
      }
    });
  }
}));

export default authStore;
